import { ListChecks } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TaskCompletionEvents } from "@/lib/render-items";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

interface TaskCompletionEventsBannerProps {
  event: TaskCompletionEvents;
  className?: string;
}

/** Render a generic batched session wake without assuming every future kind. */
export function TaskCompletionEventsBanner({
  event,
  className,
}: TaskCompletionEventsBannerProps) {
  const { t } = useTranslation();

  return (
    <Alert
      className={cn(
        "mx-4 mb-4 w-auto overflow-hidden border-primary/20 bg-primary/5 py-2.5 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2",
        className,
      )}
      data-testid="task-completion-events"
      role="status"
    >
      <ListChecks className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
      <AlertDescription className="min-w-0 overflow-hidden text-foreground">
        <AlertTitle className="mb-1">
          {event.isFallback
            ? t("chat:taskCompletion.fallback")
            : t("chat:taskCompletion.title")}
        </AlertTitle>
        {event.events.length > 0 && (
          <ul className="space-y-0.5 text-sm text-muted-foreground">
            {event.events.map((task) => (
              <li key={task.kind + ":" + task.taskId}>
                <span className="break-words text-foreground">
                  {task.description || t("chat:taskCompletion.unnamed")}
                </span>
                {" · "}
                {t("chat:taskCompletion.status." + task.status)}
              </li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}
